import { Fragment, useState, useEffect, useCallback, useContext } from "react";
import { Alert } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, constants, UserContext } from "../utils";

export default function KinotekMovementAdmin() {
  // const [selectedOrg, setSelectedOrg] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showInstructions, setShowInstructions] = useState(false);

  const [orgs, setOrgs] = useState([]);
  const [editedMovements, setEditedMovements] = useState([]);
  const userCtx = useContext(UserContext);

  var isSysAdmin = userCtx?.currentUser?.isSystemAdminUser;

  const loadKinotekMovements = useCallback(() => {
    api
      .post(`Kinotek/GetKinotekMovementList`, {
        activeOnly: true,
        sortField: "name",
        sortDirection: "asc",
      })
      .then((response) => {
        const movements = response.data || [];
        setEditedMovements(movements.map((m) => ({ ...m })));
      })
      .catch((error) => {
        console.error("Error loading kinotek movements:", error);
        showMessage("Failed to load Kinotek movements", true);
      });
  }, []);

  useEffect(() => {
    loadKinotekMovements();
  }, [loadKinotekMovements]);

  const showMessage = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setMessage(null);
    } else {
      setMessage(msg);
      setError(null);
    }

    setTimeout(() => {
      setError(null);
      setMessage(null);
    }, 5000);
  };

  const orgOptions = orgs.map((org) => ({
    value: org.id,
    label: org.name,
  }));

  const handleOrgChange = (callbackData) => {
    setError(null);
    setMessage(null);
    setValidationErrors([]);
  };

  const getDifficultyFieldName = (difficultyLevel) => {
    const fieldMapping = {
      [constants.KINOTEK_DIFFICULTY_LEVELS.NEGATIVE_THREE]:
        "kinotekNegativeThreeDifficultyThresholdScore",
      [constants.KINOTEK_DIFFICULTY_LEVELS.NEGATIVE_TWO]:
        "kinotekNegativeTwoDifficultyThresholdScore",
      [constants.KINOTEK_DIFFICULTY_LEVELS.NEGATIVE_ONE]:
        "kinotekNegativeOneDifficultyThresholdScore",
      [constants.KINOTEK_DIFFICULTY_LEVELS.BASE]:
        "kinotekBaseDifficultyThresholdScore",
      [constants.KINOTEK_DIFFICULTY_LEVELS.PLUS_ONE]:
        "kinotekOneDifficultyThresholdScore",
      [constants.KINOTEK_DIFFICULTY_LEVELS.PLUS_TWO]:
        "kinotekTwoDifficultyThresholdScore",
      [constants.KINOTEK_DIFFICULTY_LEVELS.PLUS_THREE]:
        "kinotekThreeDifficultyThresholdScore",
    };

    return (
      fieldMapping[difficultyLevel] ||
      fieldMapping[constants.KINOTEK_DIFFICULTY_LEVELS.BASE]
    );
  };

  const getThresholdValue = useCallback((movement, difficultyLevel) => {
    const fieldName = getDifficultyFieldName(difficultyLevel);
    const value = movement[fieldName];
    return value === null || value === undefined ? 0 : value;
  }, []);

  const getNextDifficultyLevel = (difficultyLevel) => {
    return constants.KINOTEK_DIFFICULTY_NEXT_LEVEL[difficultyLevel] || null;
  };

  const getPreviousDifficultyLevel = (difficultyLevel) => {
    const currentIndex =
      constants.KINOTEK_DIFFICULTY_ORDER.indexOf(difficultyLevel);
    if (currentIndex <= 0) return null;
    return constants.KINOTEK_DIFFICULTY_ORDER[currentIndex - 1];
  };

  const validateThreshold = useCallback(
    (movement, difficultyLevel, currentValue) => {
      const numericCurrentValue = currentValue === "" ? 0 : currentValue;

      if (numericCurrentValue < 0 || numericCurrentValue > 100) {
        return {
          isValid: false,
          error: `Value must be between 0 and 100`,
        };
      }

      const previousLevel = getPreviousDifficultyLevel(difficultyLevel);
      if (previousLevel) {
        const previousValue = getThresholdValue(movement, previousLevel);
        const numericPreviousValue = previousValue === "" ? 0 : previousValue;

        if (
          numericCurrentValue !== 0 &&
          numericPreviousValue !== 0 &&
          numericCurrentValue <= numericPreviousValue
        ) {
          return {
            isValid: false,
            error: `Value must be greater than the ${constants.KINOTEK_DIFFICULTY_DISPLAY_LABELS[previousLevel]} threshold (${numericPreviousValue})`,
          };
        }
      }

      const nextLevel = getNextDifficultyLevel(difficultyLevel);
      if (!nextLevel) return { isValid: true, error: null };

      const nextValue = getThresholdValue(movement, nextLevel);
      const numericNextValue = nextValue === "" ? 0 : nextValue;

      if (numericCurrentValue === 0 && numericNextValue === 0) {
        return { isValid: true, error: null };
      }

      if (numericCurrentValue >= numericNextValue) {
        return {
          isValid: false,
          error: `Value cannot be greater than or equal to the ${constants.KINOTEK_DIFFICULTY_DISPLAY_LABELS[nextLevel]} threshold (${numericNextValue})`,
        };
      }
      return { isValid: true, error: null };
    },
    [getThresholdValue]
  );

  const handleThresholdChange = (movementId, difficultyLevel, value) => {
    const numericValue = value === "" ? "" : parseInt(value) || 0;
    const fieldName = getDifficultyFieldName(difficultyLevel);

    setEditedMovements((prev) =>
      prev.map((movement) =>
        movement.id === movementId
          ? { ...movement, [fieldName]: numericValue }
          : movement
      )
    );

    if (value === "") {
      return; // Don't save empty values - let user finish typing
    }

    const finalNumericValue = parseInt(value) || 0;
    if (finalNumericValue < 0 || finalNumericValue > 100) {
      return;
    }

    const movementToSave = editedMovements.find((m) => m.id === movementId);
    if (movementToSave) {
      const updatedMovement = {
        ...movementToSave,
        [fieldName]: finalNumericValue,
      };

      const validation = validateThreshold(
        updatedMovement,
        difficultyLevel,
        finalNumericValue
      );
      if (validation.isValid) {
        saveSingleMovement(updatedMovement);
      }
    }
  };

  const saveSingleMovement = (movement) => {
    const payload = {
      movement: {
        id: movement.id,
        name: movement.name,
        kinotekNegativeThreeDifficultyThresholdScore:
          movement.kinotekNegativeThreeDifficultyThresholdScore || 0,
        kinotekNegativeTwoDifficultyThresholdScore:
          movement.kinotekNegativeTwoDifficultyThresholdScore || 0,
        kinotekNegativeOneDifficultyThresholdScore:
          movement.kinotekNegativeOneDifficultyThresholdScore || 0,
        kinotekBaseDifficultyThresholdScore:
          movement.kinotekBaseDifficultyThresholdScore || 0,
        kinotekOneDifficultyThresholdScore:
          movement.kinotekOneDifficultyThresholdScore || 0,
        kinotekTwoDifficultyThresholdScore:
          movement.kinotekTwoDifficultyThresholdScore || 0,
        kinotekThreeDifficultyThresholdScore:
          movement.kinotekThreeDifficultyThresholdScore || 0,
      },
    };

    api
      .post("Kinotek/SaveKinotekMovement", payload)
      .then(() => {
        showMessage("Movement difficulty scores saved successfully");
        setEditedMovements((prev) =>
          prev.map((m) => (m.id === movement.id ? { ...movement } : m))
        );
      })
      .catch((error) => {
        console.error("Error saving movement:", error);
        showMessage("Failed to save movement difficulty scores", true);
      });
  };

  const collectValidationErrors = useCallback(() => {
    const errors = [];

    editedMovements.forEach((movement) => {
      constants.KINOTEK_DIFFICULTY_ORDER.forEach((difficultyLevel) => {
        const currentValue = getThresholdValue(movement, difficultyLevel);
        const validation = validateThreshold(
          movement,
          difficultyLevel,
          currentValue
        );

        if (!validation.isValid) {
          const difficultyLabel =
            constants.KINOTEK_DIFFICULTY_DISPLAY_LABELS[difficultyLevel];
          errors.push({
            movement: movement.name,
            difficulty: difficultyLabel,
            error: validation.error,
          });
        }
      });
    });

    return errors;
  }, [editedMovements, getThresholdValue, validateThreshold]);

  useEffect(() => {
    const errors = collectValidationErrors();
    setValidationErrors(errors);
  }, [collectValidationErrors]);

  if (!isSysAdmin) {
    return (
      <DesktopContainer>
        <Fragment>
          <Alert color="danger">
            You do not have permission to access this page.
          </Alert>
        </Fragment>
      </DesktopContainer>
    );
  }

  return (
    <DesktopContainer>
      <Fragment>
        <div className="kinotek-movement-admin-page-container">
          <div className="action-buttons">
            <div className="org-selection-kinotek-integration">
              <div>
                <strong>Kinotek Movement Administration:</strong>
              </div>
            </div>
          </div>

          <div className="messages-container">
            {message && (
              <Alert color="success" className="mb-3">
                {message}
              </Alert>
            )}
            {error && (
              <Alert color="danger" className="mb-3">
                {error}
              </Alert>
            )}
            {validationErrors.length > 0 && (
              <Alert color="warning" className="mb-3">
                <strong>Validation Errors:</strong>
                <ul className="mb-0 mt-2">
                  {validationErrors.map((validationError, index) => (
                    <li key={index}>
                      <strong>{validationError.movement}</strong> -{" "}
                      {validationError.difficulty}: {validationError.error}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
          </div>

          <div className="movement-table-container">
            {/* Toggle button for instructions */}
            <div style={{ marginBottom: "15px" }}>
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => setShowInstructions(!showInstructions)}
                style={{
                  color: "#007bff",
                  textDecoration: "none",
                  border: "none",
                  background: "none",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                <i
                  className={`fas ${
                    showInstructions ? "fa-chevron-down" : "fa-chevron-right"
                  }`}
                  style={{ marginRight: "8px", fontSize: "14px" }}
                ></i>
                {showInstructions ? "Hide" : "Show"} How to Use:
              </button>
            </div>

            {/* Instructions block */}
            {showInstructions && (
              <div
                className="instructions-container"
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  padding: "20px",
                  marginBottom: "20px",
                }}
              >
                <h4 style={{ color: "#495057", marginBottom: "15px" }}>
                  <i
                    className="fas fa-info-circle"
                    style={{ marginRight: "8px", color: "#007bff" }}
                  ></i>
                  How to Use Difficulty Threshold Values
                </h4>
                <div style={{ color: "#6c757d", lineHeight: "1.6" }}>
                  <p style={{ marginBottom: "12px" }}>
                    Each input field specifies the&nbsp;
                    <strong>threshold score</strong> for determining SPP client
                    movement difficulty levels when processing Kinotek movement
                    assessments. Client movement difficulties are related
                    through movement categories.
                  </p>
                  <p style={{ marginBottom: "12px" }}>
                    <strong>
                      How Kinotek scores are evaluated (in order):
                    </strong>
                  </p>
                  <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
                    <li style={{ marginBottom: "8px" }}>
                      <strong>Score = 100:</strong> Always assigned to{" "}
                      <strong>Difficulty +3</strong>
                    </li>
                    <li style={{ marginBottom: "8px" }}>
                      <strong>Score = 0:</strong> Always assigned to{" "}
                      <strong>Difficulty -3</strong>
                    </li>
                    <li style={{ marginBottom: "8px" }}>
                      <strong>
                        Score ≤ Difficulty -3 threshold (and ≠ 0):
                      </strong>{" "}
                      Assigned to <strong>Difficulty -3</strong>
                    </li>
                    <li style={{ marginBottom: "8px" }}>
                      <strong>Score ≤ Difficulty -2 threshold:</strong> Assigned
                      to <strong>Difficulty -2</strong>
                    </li>
                    <li style={{ marginBottom: "8px" }}>
                      <strong>Score ≤ Difficulty -1 threshold:</strong> Assigned
                      to <strong>Difficulty -1</strong>
                    </li>
                    <li style={{ marginBottom: "8px" }}>
                      <strong>Score ≤ Base threshold:</strong> Assigned to{" "}
                      <strong>Base Difficulty</strong>
                    </li>
                    <li style={{ marginBottom: "8px" }}>
                      <strong>Score ≤ Difficulty +1 threshold:</strong> Assigned
                      to <strong>Difficulty +1</strong>
                    </li>
                    <li style={{ marginBottom: "8px" }}>
                      <strong>Score ≤ Difficulty +2 threshold:</strong> Assigned
                      to <strong>Difficulty +2</strong>
                    </li>
                    <li style={{ marginBottom: "8px" }}>
                      <strong>Score ≤ Difficulty +3 threshold:</strong> Assigned
                      to <strong>Difficulty +3</strong>
                    </li>
                    <li style={{ marginBottom: "8px" }}>
                      <strong>
                        Score &gt; Difficulty +3 threshold (and ≠ 100):
                      </strong>{" "}
                      Assigned to <strong>Difficulty +3</strong>
                    </li>
                  </ul>
                  <p style={{ marginBottom: "0", fontStyle: "italic" }}>
                    <strong>Example:</strong> If thresholds are set as -3=10,
                    -2=20, -1=30, Base=40, +1=50, +2=60, +3=70, then a Kinotek
                    score of 35 would be assigned Base Difficulty (first
                    threshold it's ≤).
                  </p>
                </div>
              </div>
            )}

            <table className="kinotek-movement-admin-table table table-striped">
              <thead>
                <tr>
                  <th
                    style={{
                      width: "300px",
                      borderRight: "1px solid white",
                      backgroundColor: "#6c757d",
                      color: "white",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                    }}
                  >
                    Kinotek Movement
                  </th>
                  {constants.KINOTEK_DIFFICULTY_ORDER.map((difficultyLevel) => (
                    <th
                      key={difficultyLevel}
                      style={{
                        width: "120px",
                        borderRight: "1px solid white",
                        backgroundColor: "#6c757d",
                        color: "white",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        textAlign: "center",
                      }}
                    >
                      {
                        constants.KINOTEK_DIFFICULTY_DISPLAY_LABELS[
                          difficultyLevel
                        ]
                      }
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {editedMovements.map((movement) => (
                  <tr key={movement.id}>
                    <td
                      style={{
                        padding: "8px",
                        verticalAlign: "middle",
                        fontWeight: "bold",
                      }}
                    >
                      {movement.name}
                    </td>
                    {constants.KINOTEK_DIFFICULTY_ORDER.map(
                      (difficultyLevel) => (
                        <td
                          key={`${movement.id}-${difficultyLevel}`}
                          style={{
                            padding: "8px",
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          <DifficultyScoreInput
                            movement={movement}
                            difficultyLevel={difficultyLevel}
                            onThresholdChange={handleThresholdChange}
                            getThresholdValue={getThresholdValue}
                            validateThreshold={validateThreshold}
                          />
                        </td>
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Fragment>
    </DesktopContainer>
  );
}

const DifficultyScoreInput = ({
  movement,
  difficultyLevel,
  onThresholdChange,
  getThresholdValue,
  validateThreshold,
}) => {
  const currentValue = getThresholdValue(movement, difficultyLevel);

  const validation = validateThreshold(movement, difficultyLevel, currentValue);
  const displayValue = currentValue === "" ? "" : currentValue;

  return (
    <input
      type="number"
      value={displayValue}
      onChange={(e) =>
        onThresholdChange(movement.id, difficultyLevel, e.target.value)
      }
      className={`threshold-input ${validation.isValid ? "" : "error-input"}`}
      style={{
        width: "80px",
        padding: "4px",
        border: validation.isValid ? "1px solid #ccc" : "2px solid #dc3545",
        borderRadius: "4px",
      }}
    />
  );
};
